import React, { useState } from "react";

import { toast } from "react-toastify";
import { KeyRound, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// A credencial é o segredo que a ferramenta usa para chamar a API de fora.
// O segredo nunca volta do servidor: a tela mostra o nome, o tipo e os quatro
// últimos caracteres, que bastam para saber qual é.
const TIPOS = [
	{ value: "bearer", rotulo: "Bearer token" },
	{ value: "basic", rotulo: "Usuário e senha" },
	{ value: "header", rotulo: "Cabeçalho" },
	{ value: "query", rotulo: "Parâmetro na URL" },
];

const rotuloDoTipo = (tipo) =>
	TIPOS.find((t) => t.value === tipo)?.rotulo || tipo;

const FORMULARIO_VAZIO = {
	id: null,
	name: "",
	type: "bearer",
	headerName: "",
	secret: "",
};

const AbaCredenciais = ({ credenciais = [], aoMudar, aviso }) => {
	const [modal, setModal] = useState(false);
	const [form, setForm] = useState(FORMULARIO_VAZIO);
	const [salvando, setSalvando] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);

	// Só o cabeçalho e o parâmetro de URL dizem onde o segredo entra.
	const precisaDeNome = form.type === "header" || form.type === "query";

	const abrirNova = () => {
		setForm(FORMULARIO_VAZIO);
		setModal(true);
	};

	const abrirEdicao = (credencial) => {
		setForm({
			id: credencial.id,
			name: credencial.name || "",
			type: credencial.type || "bearer",
			headerName: credencial.headerName || "",
			// O segredo não vem do servidor: campo vazio quer dizer "mantém".
			secret: "",
		});
		setModal(true);
	};

	const salvar = async () => {
		// O backend exige nome com dois ou mais caracteres: avisar aqui evita o
		// erro seco vindo do servidor.
		if (form.name.trim().length < 2) {
			toast.error("Dê um nome para a credencial.");
			return;
		}

		// O cabeçalho e o parâmetro de URL dizem onde o segredo entra: sem
		// isso o servidor recusa a credencial.
		if (precisaDeNome && !form.headerName.trim()) {
			toast.error(
				form.type === "query"
					? "Diga o nome do parâmetro onde o segredo entra."
					: "Diga o nome do cabeçalho onde o segredo entra."
			);
			return;
		}

		setSalvando(true);
		try {
			if (form.id) {
				// O tipo não muda depois de criada: o backend guarda o segredo
				// cifrado e a forma de usá-lo no pedido.
				await api.put(`/ai/credentials/${form.id}`, {
					name: form.name.trim(),
					headerName: precisaDeNome ? form.headerName.trim() : null,
					secret: form.secret,
				});
			} else {
				await api.post("/ai/credentials", {
					name: form.name.trim(),
					type: form.type,
					headerName: precisaDeNome ? form.headerName.trim() : null,
					secret: form.secret,
				});
			}

			toast.success(form.id ? "Credencial atualizada." : "Credencial criada.");
			setModal(false);
			aoMudar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async () => {
		try {
			await api.delete(`/ai/credentials/${selecionada.id}`);
			toast.success("Credencial removida.");
			aoMudar();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	return (
		<div className="space-y-3">
			<ConfirmationModal
				title={selecionada && `Remover ${selecionada.name}?`}
				open={Boolean(confirmOpen)}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={excluir}
				confirmLabel="Remover"
			>
				A ferramenta que usava esta credencial continua existindo e passa a
				falhar com 401 até você apontá-la para outra.
			</ConfirmationModal>

			{aviso ? (
				<div className="rounded-xl border bg-card py-16 text-center">
					<KeyRound className="mx-auto h-8 w-8 text-muted-foreground/50" />
					<p className="mt-3 text-sm font-medium">{aviso}</p>
					<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
						Credenciais e ferramentas fazem parte do módulo de agentes de IA.
						Fale com quem administra a sua conta para liberar o módulo.
					</p>
				</div>
			) : (
				<>
					<div className="flex flex-wrap items-center gap-2">
						<p className="min-w-0 flex-1 text-xs leading-relaxed text-muted-foreground">
							O segredo que a ferramenta usa para chamar a API de fora. Fica
							cifrado no servidor e nunca é exibido de volta.
						</p>
						<Button className="h-9" onClick={abrirNova}>
							<Plus className="h-4 w-4" />
							<span className="hidden sm:inline">Nova credencial</span>
							<span className="sm:hidden">Nova</span>
						</Button>
					</div>

					{credenciais.length === 0 ? (
						<div className="rounded-xl border bg-card py-16 text-center">
							<KeyRound className="mx-auto h-8 w-8 text-muted-foreground/50" />
							<p className="mt-3 text-sm font-medium">
								Nenhuma credencial cadastrada
							</p>
							<p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
								Crie uma credencial e aponte uma ferramenta para ela. Sem
								credencial, a ferramenta só funciona em API aberta.
							</p>
						</div>
					) : (
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{credenciais.map((credencial) => (
								<div
									key={credencial.id}
									className="flex flex-col rounded-xl border bg-card p-4"
								>
									<div className="flex items-start gap-2.5">
										<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
											<KeyRound className="h-4 w-4" />
										</span>
										<div className="min-w-0">
											<p className="truncate font-medium">{credencial.name}</p>
											<p className="truncate text-xs text-muted-foreground">
												{rotuloDoTipo(credencial.type)}
											</p>
										</div>
									</div>

									<dl className="mt-3 space-y-1 text-xs">
										<div className="flex justify-between gap-2">
											<dt className="text-muted-foreground">Segredo</dt>
											<dd className="font-medium tabular-nums">
												{credencial.last4 ? `••••${credencial.last4}` : "—"}
											</dd>
										</div>
										{credencial.headerName && (
											<div className="flex justify-between gap-2">
												<dt className="text-muted-foreground">Onde entra</dt>
												<dd className="truncate font-medium">
													{credencial.headerName}
												</dd>
											</div>
										)}
									</dl>

									<div className="mt-3 flex flex-wrap gap-1 border-t pt-3">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											aria-label={`Editar ${credencial.name}`}
											onClick={() => abrirEdicao(credencial)}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-destructive"
											aria-label={`Remover ${credencial.name}`}
											onClick={() => {
												setSelecionada(credencial);
												setConfirmOpen(true);
											}}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							))}
						</div>
				)}
				</>
			)}

			<Dialog open={modal} onOpenChange={() => setModal(false)}>
				<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{form.id ? "Editar credencial" : "Nova credencial"}
						</DialogTitle>
						<DialogDescription>
							O segredo fica cifrado no servidor. Depois de criado, ele nunca
							mais é exibido — nem por você.
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label htmlFor="credencial-nome">Nome</Label>
							<Input
								id="credencial-nome"
								value={form.name}
								onChange={(e) =>
									setForm({ ...form, name: e.target.value })
								}
								placeholder="API do banco"
							/>
						</div>

						<div className="space-y-1.5">
							<Label>Tipo</Label>
							<Select
								value={form.type}
								onValueChange={(v) => setForm({ ...form, type: v })}
								disabled={Boolean(form.id)}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{TIPOS.map((tipo) => (
										<SelectItem key={tipo.value} value={tipo.value}>
											{tipo.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className="text-xs text-muted-foreground">
								{form.type === "basic"
									? "No segredo, escreva usuário:senha."
									: form.id
										? "O tipo não muda depois que a credencial é criada."
										: "Como o segredo entra no pedido para a API de fora."}
							</p>
						</div>

						{precisaDeNome && (
							<div className="space-y-1.5">
								<Label htmlFor="credencial-cabecalho">
									{form.type === "query"
										? "Nome do parâmetro"
										: "Nome do cabeçalho"}
								</Label>
								<Input
									id="credencial-cabecalho"
									value={form.headerName}
									onChange={(e) =>
										setForm({ ...form, headerName: e.target.value })
									}
									placeholder={
										form.type === "query" ? "token" : "X-Api-Key"
									}
								/>
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="credencial-segredo">Segredo</Label>
							<Input
								id="credencial-segredo"
								type="password"
								value={form.secret}
								onChange={(e) =>
									setForm({ ...form, secret: e.target.value })
								}
								placeholder={
									form.id
										? "deixe em branco para manter"
										: "Cole o segredo aqui"
								}
							/>
							<p className="text-xs text-muted-foreground">
								Nunca é exibido de volta. Em branco na edição, o segredo
								guardado continua valendo.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button variant="outline" onClick={() => setModal(false)}>
							Cancelar
						</Button>
						<Button
							onClick={salvar}
							disabled={salvando || !form.name.trim() || (!form.id && !form.secret)}
						>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							{form.id ? "Salvar" : "Criar credencial"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default AbaCredenciais;
